/**
 * 地理位置采集工具函数
 * 用于在VSS打印场景中采集供应商发运地址（IP定位）
 *
 * 打印场景：
 * - 预采单打印标签
 * - 预采单设备打印标签
 * - 采购单打印标签
 * - 采购单设备打印标签
 * - 采购单箱码打印标签
 *
 * 定位方案：
 * 使用美团地图 GeoLocateControl 定位，PC 端优先 IP 定位，IP 定位失败后降级到 HTML5 浏览器定位。
 * 打印前会检查浏览器位置权限，用户已拒绝授权时弹提示并阻断打印流程。
 * 定位失败（非权限问题）静默处理，不阻断打印流程，跳过上报接口。
 */

import { getCookie } from '@mcfe/tools';
import { reportLx } from '@lsfe/tools';

// 美团地图 JSAPI Key
const MT_MAP_KEY = 'm377939ae0a241d0811d5db7c73fd58j';

// 灵犀埋点 BID / CID：SDK 加载失败、定位成功、定位失败共用同一个 bid，通过参数区分
const GEO_LX_BID = 'b_chaoshi_8zmf6dn8_mc';
const GEO_LX_CID = { cid: 'c_chaoshi_8zmf6dn8' };

/**
 * 获取灵犀埋点公共参数
 * @param {string} customerId 供应商ID
 * @param {string} transId 业务订单号
 * @param {string|number} poiId 仓 POI ID，对应 click_position
 * @param {string} pageType 页面来源，'preOrder' 或 'purchase'
 * @returns {{ customer_id: string, VSS_account: string, TRANS_ID: string, click_position: string, page_type: string }}
 */
function getGeoLxBaseParams(customerId, transId, poiId, pageType) {
    return {
        customer_id: customerId || '',
        VSS_account: getCookie('msid') || '',
        TRANS_ID: transId || '',
        click_position: poiId || '',
        page_type: pageType || '',
    };
}

/**
 * 缓存 SDK 加载的 Promise，避免多次点击时重复注入 <script> 标签。
 * 首次加载后后续调用直接复用同一个 Promise。
 */
let mtMapLoadPromise = null;

/**
 * 动态加载美团地图 JSAPI（含 GeoLocateControl 插件）
 * 只加载一次，后续复用缓存
 * @returns {Promise<void>}
 */
function loadMtMapScript(customerId, transId, poiId, pageType) {
    if (!MT_MAP_KEY) {
        return Promise.reject(new Error('未配置美团地图 Key'));
    }
    // 已有缓存的 Promise，直接复用（无论加载是否完成）
    if (mtMapLoadPromise) {
        return mtMapLoadPromise;
    }
    mtMapLoadPromise = new Promise((resolve, reject) => {
        // SDK 可能已被其他模块加载，window.mtdpMap 存在则直接 resolve
        if (window.mtdpMap) {
            resolve();
            return;
        }
        // 动态创建 <script> 标签注入美团地图 SDK
        // plugin=GeoLocateControl 表示同时加载定位控件插件
        const script = document.createElement('script');
        script.crossOrigin = 'anonymous';
        script.src = `https://lbsapi.meituan.com/jsapi/maps?v=2.8.0&key=${MT_MAP_KEY}&plugin=GeoLocateControl`;
        script.onload = () => resolve();
        script.onerror = () => {
            // 加载失败时清空缓存，下次调用可重试
            // 注意：本地 HTTP 环境因 Mixed Content 策略会触发此分支
            mtMapLoadPromise = null;
            // 灵犀埋点：SDK 加载失败
            reportLx('moduleClick', GEO_LX_BID, {
                ...getGeoLxBaseParams(customerId, transId, poiId, pageType),
                type: 'sdk_load_error',
                error_msg: 'mt_map_sdk_load_failed',
                sdk_url: script.src,
            }, GEO_LX_CID);
            reject(new Error('获取定位失败，请重试'));
        };
        document.head.appendChild(script);
    });
    return mtMapLoadPromise;
}

/**
 * 使用美团地图 GeoLocateControl 获取当前位置（IP 定位）
 *
 * 工作原理：
 * GeoLocateControl 必须依附在 Map 实例上才能工作，因此需要先创建一个隐藏地图。
 * IP 定位在服务端完成，不会触发浏览器的原生位置权限弹窗。
 *
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number, timestamp: number, locationType: string}>}
 */
function getMtMapGeoLocation(customerId, transId, poiId, pageType) {
    return new Promise((resolve, reject) => {
        loadMtMapScript(customerId, transId, poiId, pageType).then(() => {
            // GeoLocateControl 必须挂载在 Map 实例上，因此创建一个 1×1 像素的隐藏容器。
            // 用户完全不可见，定位完成后立即销毁。
            const container = document.createElement('div');
            container.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1;';
            document.body.appendChild(container);

            let map;
            try {
                map = new window.mtdpMap.Map({
                    container, // 挂载到上面创建的隐藏 div
                    // 初始中心点（北京天安门附近），仅占位，不影响 IP 定位结果
                    center: [116.397, 39.908],
                    zoom: 10, // 初始缩放级别，仅占位
                });
            } catch (e) {
                document.body.removeChild(container);
                reject(new Error('获取定位失败，请重试'));
                return;
            }

            // 定位完成或失败后销毁地图实例和容器，避免内存泄漏
            const cleanup = () => {
                try { map.remove(); } catch (e) { /* ignore */ }
                if (container.parentNode) document.body.removeChild(container);
            };

            // 创建定位控件，所有 UI 相关配置均关闭，仅用于获取坐标
            const geoControl = new window.mtdpMap.GeoLocateControl({
                map,
                convert: true, // true = 返回美团地图坐标（GCJ-02），false = 返回浏览器原始坐标（WGS84）
                showMarker: false, // 不在地图上显示定位点
                showCircle: false, // 不显示精度圆圈
                panToLocation: false, // 定位后不移动地图中心
                zoomToAccuracy: false, // 定位后不自动调整缩放级别
            });

            geoControl.getCurrentPosition(
                // 定位成功回调
                (result) => {
                    cleanup();
                    const { lng, lat } = result.position;
                    const accuracy = result.accuracy || 0;
                    const locationType = result.location_type || 'ip';
                    console.log('定位成功:', result);
                    resolve({
                        latitude: lat,
                        longitude: lng,
                        accuracy, // 定位精度（米），IP 定位精度较低
                        timestamp: Date.now(),
                        locationType, // 定位来源：'ip' / 'html5'
                    });
                    // 灵犀埋点：定位成功
                    reportLx('moduleClick', GEO_LX_BID, {
                        ...getGeoLxBaseParams(customerId, transId, poiId, pageType),
                        type: 'geo_locate_success',
                        upload_status: 'success',
                        accuracy,
                        location_type: locationType,
                    }, GEO_LX_CID);
                },
                // 定位失败回调（err.info 为美团地图内部错误码）
                async (err) => {
                    cleanup();
                    console.error('[GeoLocation] 美团地图定位失败:', err && err.info);

                    reject(new Error('获取定位失败，请重试'));

                    // 灵犀埋点：定位失败
                    reportLx('moduleClick', GEO_LX_BID, {
                        ...getGeoLxBaseParams(customerId, transId, poiId, pageType),
                        type: 'geo_locate_error',
                        upload_status: 'failed',
                        accuracy: 0,
                        location_type: (err && err.location_type) || 'unknown',
                        error_msg: 'mt_map_geo_locate_failed',
                        error_info: (err && err.info) || 'unknown',
                    }, GEO_LX_CID);
                },
            );
        }).catch(reject);
    });
}

/**
 * 在打印前执行地理位置采集
 *
 * - 定位成功：返回真实经纬度对象
 * - 定位失败（SDK 加载失败、网络问题等）：返回 null，调用方应跳过上报接口
 *
 * @returns {Promise<{latitude: number, longitude: number, accuracy: number, timestamp: number, locationType: string}|null>}
 */
export async function collectGeoBeforePrint(customerId, transId, poiId, pageType) {
    try {
        return await getMtMapGeoLocation(customerId, transId, poiId, pageType);
    } catch (err) {
        // 定位失败时静默处理，不阻断打印流程（权限检查已在 checkGeoPermission 前置完成）
        console.error('[GeoLocation] 定位采集失败:', err.message);
        return null;
    }
}

/**
 * 从上报接口的 catch error 中提取可展示给用户的错误信息
 *
 * 背景：@lsfe/components 的 filterData 拦截器在某些情况下会抛出异常，
 * 此时原始响应体挂在 err.data 上，需要从中提取 msg 字段。
 *
 * @param {Error} err
 * @returns {string}
 */
export function getReportErrorMsg(err) {
    // filterData 崩溃时，原始响应挂在 err.data 上
    if (err && err.data) {
        const res = err.data;
        return res.msg || (res.error && (res.error.msg || res.error.errMsg || res.error.message)) || '位置信息上报失败';
    }
    return (err && err.message) || '位置信息上报失败';
}

/**
 * 打印前的浏览器位置权限检查（阻塞式）
 *
 * 仅在 IP 定位失败、SDK 会降级到 HTML5 定位的场景下才有意义（如外部域名 CORS 被拒）。
 * 检查规则：
 * - denied：弹出提示弹窗，用户点「确定」后打印流程中断，不允许继续打印
 * - prompt：用户尚未被问过，让 SDK 在定位时自动弹出浏览器授权窗，不阻塞
 * - granted：已授权，不阻塞
 * - 不支持 permissions API：不阻塞，走正常定位流程
 *
 * @param {Object} vm - Vue 实例，用于调用 $confirm
 * @returns {Promise<void>}
 */
export async function checkGeoPermission(vm) {
    if (!navigator.permissions) return;
    try {
        const { state } = await navigator.permissions.query({ name: 'geolocation' });
        if (state === 'denied') {
            // 弹出提示弹窗，告知用户去浏览器设置中手动开启位置权限
            // 用户点「确定」后流程中断，不允许继续打印
            await vm.$alert(
                '浏览器定位权限已被禁用，无法完成打印前的位置采集。<br/>'
                + '<b>请在浏览器设置中开启当前网站的定位权限，授权后进行打印。</b><br/><br/>'
                + '如使用 Google Chrome 浏览器，可参考以下步骤开启定位权限：'
                + '<ol style="margin:8px 0 0 16px;padding:0;line-height:2">'
                + '<li>复制地址：chrome://settings/content/location 粘贴到 Chrome 地址栏并回车；'
                + '或点击 设置 &gt; 隐私与安全 &gt; 网站设置 &gt; 位置信息</li>'
                + '<li>在「不允许使用您的位置信息」列表中找到 https://vss.baobaoaichi.cn，点击右侧删除按钮</li>'
                + '<li>刷新当前页面，重新点击打印，在弹出的授权提示中选择「访问该网站时允许」或「仅这次访问时允许」</li>'
                + '</ol>',
                '定位权限未开启',
                {
                    confirmButtonText: '知道了',
                    type: 'warning',
                    dangerouslyUseHTMLString: true,
                    showClose: false,
                    closeOnClickModal: false,
                    closeOnPressEscape: false,
                },
            );
            // $alert 关闭后抛出，阻塞打印流程
            throw new Error('geo_permission_denied');
        }
    } catch (err) {
        // 只重新抛出权限拒绝错误，其他错误（如 permissions API 不支持）静默忽略
        if (err && err.message === 'geo_permission_denied') {
            throw err;
        }
    }
}

/**
 * 采集定位并上报发运地址（fire-and-forget，不阻塞调用方）
 *
 * 调用方直接调用，无需 await，打印弹窗立即打开，定位与上报在后台异步执行。
 * - 定位失败：静默跳过上报
 * - 上报失败：toast 提示
 *
 * 注意：权限检查（checkGeoPermission）应在调用此函数之前完成，
 * 本函数不再重复处理 geo_permission_denied 错误。
 *
 * @param {Object} options
 * @param {Object} options.vm          - Vue 实例（Options API 传 this，Composition API 传 proxy）
 * @param {string|number} options.poiId      - 仓 POI ID，同时作为埋点 click_position
 * @param {string|number} options.skuId      - SKU ID
 * @param {string|number} options.supplierId - 供货规则 ID
 * @param {string} options.transId     - 业务订单号（采购单号/预采单号），用于埋点 TRANS_ID
 * @param {string} options.pageType    - 页面来源：'preOrder'（预采页面）或 'purchase'（采购页面）
 */
export function collectAndReportGeo({
    vm, poiId, skuId, supplierId, transId, pageType,
}) {
    (async () => {
        try {
            const { customerId = '' } = await vm.__querySupplierInfoByBizAccountId();
            const location = await collectGeoBeforePrint(customerId, transId, poiId, pageType);
            if (!location) return;

            await vm.$hPost(
                '/api/vss/second/shepherd/supplierPlaceOfOrigin/reportSupplierPlaceOfOrigin',
                {
                    obtainTimestamp: location.timestamp || Date.now(),
                    latitude: String(location.latitude),
                    longitude: String(location.longitude),
                    poiId,
                    skuId,
                    supplierId,
                },
            );
        } catch (err) {
            vm.$message.error(getReportErrorMsg(err));
        }
    })();
}
